<template>
  <div>
    <div class="mb-6 w-full d-flex justify-space-between align-center">
      <TeamTabs
        v-if="tabs.length > 1"
        :tabs="tabs"
        :loading="listLoading"
      />
      <div
        v-if="tabName === 'platform-permissions' && !listLoading"
        class="d-flex ga-4"
      >
        <DpplNewButton
          v-if="!integrated || allowIntegratedUserInvite"
          color="primary"
          :loading="listLoading || inviteLoading.length > 0"
          @click="setModal({ field: 'settings', value: true })"
        >
          Invite User
        </DpplNewButton>
        <DpplNewButton
          v-if="integrated"
          color="primary"
          :disabled="!selectedCount"
          :loading="listLoading || inviteLoading.length > 0"
          @click="sendMassInvite(selectedCount)"
        >
          Onboard User{{ selectedCount > 1 ? 's' : '' }}
        </DpplNewButton>
      </div>
      <DpplNewButton
        v-else-if="tabName === 'demographic-permissions' && !listLoading"
        color="primary"
        :loading="listLoading"
        @click="showGrantAccessModal = true"
      >
        Grant Full Report Access
      </DpplNewButton>
    </div>
    <div v-if="tabName === 'platform-permissions'" class="my-3 w-full d-flex justify-space-between align-start">
      <TeamSummary
        :totals="totals"
        :loading="listLoading"
      />
      <TeamFilterBar
        :active-search="searchTerm"
        :loading="filtersLoading || listLoading"
      />
    </div>
    <router-view
      v-slot="{ Component }"
      @track-page-load="$emit('track-page-load', $event)"
      @insights-tracking-event="handleTrackingEvent($event)"
    >
      <component :is="Component" />
    </router-view>
    <GrantAccessModal
      v-model="showGrantAccessModal"
      @granted-access="handleGrantAccessModalUpdate"
    />
  </div>
</template>

<script setup>
import {
  ref, computed,
} from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useTracking } from '@/stores/tracking/tracking';
import { useModals } from '@/stores/modals/modals';
import { useSettingsTeam } from '@/stores/settings/team';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import TeamFilterBar from '@/components/settings/team/TeamFilterBar.vue';
import TeamSummary from '@/components/settings/team/TeamSummary.vue';
import GrantAccessModal from '@/components/settings/team/GrantAccessModal.vue';
import { DEMOGRAPHIC_SURVEY_SHOW } from '@/helpers/constants/demographicSurveyVersions';
import TeamTabs from '@/components/settings/team/TeamTabs.vue';
import { useSettingsIntegrations } from '@/stores/settings/integrations';

defineEmits(['track-page-load']);

const showGrantAccessModal = ref(false);

const route = useRoute();

const {
  listLoading, filtersLoading,
} = storeToRefs(useAppLoading());

const {
  totals, searchTerm, inviteLoading, selectedCount,
} = storeToRefs(useSettingsTeam());

const { allowIntegratedUserInvite } = storeToRefs(useSettingsIntegrations());

const { integrated, authData } = storeToRefs(useAppAuthData());
const { checkRbacPermissions } = useAppAuthData();

const { loadDemographicSurveyUsers } = useSettingsTeam();

const tabName = computed(() => route.name || 'administrators');

const tabs = computed(() => {
  const tabsArr = [{ label: 'My JobNav Recruiter Administrators', route: 'administrators' }];

  if (checkRbacPermissions(['viewTeam'])) {
    tabsArr.push({ label: 'Platform Permissions', route: 'platform-permissions' });
  }

  if (integrated.value
    && (authData.value.canDemographicSurveyReport
      && authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_SHOW)) {
    tabsArr.push({ label: 'Demographic Report Permissions', route: 'demographic-permissions' });
  }

  return tabsArr;
});

const { setModal } = useModals();
const { sendOnboardingEmails } = useSettingsTeam();

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};

const sendMassInvite = async (event) => {
  handleTrackingEvent({ action: 'massInviteSent', args: { selectedCount: event } });
  return sendOnboardingEmails(event);
};

const handleGrantAccessModalUpdate = async () => loadDemographicSurveyUsers();
</script>

<script>
export default { name: 'TeamSettings' };
</script>
